// pages/Campaigns/Gmail/GmailConnector.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLocation, useNavigate } from 'react-router-dom';
import backendURL from "../../../config";
import ListGmailEmails from "./ListGmailEmails";
// import PromptSettings from "./PromptSettings"; // No longer directly used here
import { APPLICATION_URL } from "../../../config";

export default function GmailConnector() {
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState('');
    const location = useLocation();
    const navigate = useNavigate();
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const checkStatus = useCallback(async (showLoading = true) => {
        if (showLoading) {
            setIsLoading(true);
        }
        setError('');
        const token = localStorage.getItem("access_token");
        try {
            const response = await axios.get(`${backendURL}/api/gmail/status`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setIsConnected(response.data.connected);
        } catch (err) {
            console.error("Error checking Gmail status:", err);
            setError("Failed to check connection status. Please try again.");
            setIsConnected(false);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'Gmail Connector | KlonIT';
        const params = new URLSearchParams(location.search);

        if (params.has('connected')) {
            params.delete('connected');
            window.history.replaceState(null, '', `${location.pathname}?${params}`);
            checkStatus(false).then(() => setRefreshTrigger(p => p + 1));
            return;
        }

        if (params.has('error')) {
            setError(`Authentication failed: ${params.get('error')}`);
            params.delete('error');
            window.history.replaceState(null, '', `${location.pathname}?${params}`);
        }

        checkStatus();
        }, [location.search, checkStatus]);

    const handleConnect = async () => {
        setIsConnecting(true);
        setError('');
        const userAuthToken = localStorage.getItem("access_token");
        try {
            const response = await axios.get(`${backendURL}/api/gmail/auth/start`, {
                headers: { Authorization: `Bearer ${userAuthToken}` }
            });
            if (response.data.authorization_url) {
                window.location.href = response.data.authorization_url;
            } else {
                setError("Failed to get authorization URL.");
            }
        } catch (err) {
            setError(err.response?.data?.message || "Could not connect to Gmail.");
        } finally {
            setIsConnecting(false);
        }
    };

    const handleDisconnect = async () => {
        setIsConnecting(true);
        setError('');
        const token = localStorage.getItem("access_token");
        try {
            await axios.post(`${backendURL}/api/gmail/disconnect`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIsConnected(false);
            setError('Successfully disconnected from Gmail.');
        } catch (err) {
            setError(err.response?.data?.message || "Failed to disconnect.");
        } finally {
            setIsConnecting(false);
        }
    };

    return (
        <div>
            <main className="content">
                <div className="container-fluid p-0">
                    <div className="mb-3 d-flex justify-content-between align-items-center">
                        <h1 className="h3 d-inline align-middle"><strong>Gmail Auto-Responder</strong></h1>
                        {!isLoading && isConnected && (
                            <button className="btn btn-danger" onClick={handleDisconnect} disabled={isConnecting}>
                                {isConnecting ? 'Disconnecting...' : <><i className="fa fa-unlink me-1"></i> Disconnect Gmail</>}
                            </button>
                        )}
                    </div>

                    {isLoading && <p>Loading configuration...</p>}
                    
                    {error && (
                        <div className={`alert ${error.includes("Success") ? "alert-success" : "alert-danger"} mt-3`} role="alert">
                            {error}
                        </div>
                    )}

                    {!isLoading && !isConnected && (
                        <div className="card">
                            <div className="card-body text-center">
                                <p>Connect your Gmail account to automatically process and reply to emails.</p>
                                <button className="btn btn-lg btn-primary" onClick={handleConnect} disabled={isConnecting}>
                                    {isConnecting ? 'Connecting...' : <><i className="fa fa-google me-2"></i> Connect to Gmail</>}
                                </button>
                            </div>
                        </div>
                    )}

                    {!isLoading && isConnected && (
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="card-title mb-0">Incoming Emails</h5>
                                        <p className="mb-0">Emails are checked periodically. Use the buttons below to fetch new mail or manage settings.</p>
                                    </div>
                                    <div className="card-body">
                                        <ListGmailEmails refreshTrigger={refreshTrigger} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}